import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Info, Lock, ToggleRight } from 'lucide-react';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

// Shapes mirror GET /api/v1/settings/roles. The API is authoritative for this
// page: role labels, descriptions, and every copy string are rendered straight
// from the response — nothing about roles or the elevation toggle is hardcoded.
interface RoleRow {
  key: string;
  label: string;
  description: string;
}

interface RolesCopy {
  toggle_label: string;
  toggle_description: string;
  admin_only_note: string;
}

interface RolesResponse {
  roles: RoleRow[];
  dispatcher_admin_access: boolean;
  copy: RolesCopy;
}

/*
 * Two different things share this page, and the page says which is which:
 *
 *   - The role list is REFERENCE. The API serves it from a constant
 *     (ROLE_MATRIX in settings-v2.routes.ts); nothing a tenant does changes
 *     it, and there is no endpoint that could. It is labelled as such.
 *   - dispatcher_admin_access is the one CONTROL.
 *
 * Who may flip the control: PATCH /settings/roles is requireRole('admin',
 * { strict: true }). Strict means an elevated dispatcher is refused — they
 * cannot flip the toggle that elevates them — so useCanWriteCompanySettings,
 * which admits that dispatcher, is the wrong gate here. The list is matched
 * as written, so `owner` and `super_admin` are refused too.
 *
 * GET /settings/roles carries no role gate, so every role may read the page.
 */
export default function RolesPermissionsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const role = user?.role;
  const canToggle = role === 'admin';

  // Local mirror of the persisted toggle so the switch reflects the server
  // value but can revert cleanly if the PATCH fails.
  const [enabled, setEnabled] = useState(false);

  // Same key as useCanWriteCompanySettings and ApprovalQueueScreen, so a flip
  // here is seen there.
  const { data, isLoading, isError } = useQuery<RolesResponse | null>({
    queryKey: ['settings', 'roles'],
    queryFn: async () => {
      const res = await api.settingsRoles.get();
      return unwrap<RolesResponse>(res.data);
    },
  });

  useEffect(() => {
    if (data) {
      setEnabled(!!data.dispatcher_admin_access);
    }
  }, [data]);

  const saveMutation = useMutation({
    // Body field is snake_case to match the backend Zod .strict() schema.
    mutationFn: (next: boolean) =>
      api.settingsRoles.update({ dispatcher_admin_access: next }),
    onSuccess: () => {
      // Re-fetch so the page reflects the persisted value as source of truth.
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      toast.success('Permissions updated');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Failed to update permissions');
    },
  });

  const handleToggle = () => {
    if (!canToggle || saveMutation.isPending) return;
    const prev = enabled;
    const next = !enabled;
    setEnabled(next); // optimistic
    saveMutation.mutate(next, { onError: () => setEnabled(prev) });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
      </div>
    );
  }

  const roles = data?.roles ?? [];
  const copy = data?.copy;

  // Why the switch is not live for this caller. Worded for the case, because
  // an elevated dispatcher can change most admin settings and will expect to
  // change this one too.
  const refusal =
    role === 'dispatcher'
      ? 'Only an admin can change this. A dispatcher cannot change it, even with admin-level access turned on.'
      : 'Only an admin can change this.';

  return (
    <div className="min-h-full">
      {/* The People & access workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-8">
        {isError || !data ? (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 text-center">
            <p className="text-sm text-fieldiq-text">
              Couldn&apos;t load roles &amp; permissions. Please try again.
            </p>
          </div>
        ) : (
          <>
            {/* THE REFERENCE — role descriptions from the API, not editable */}
            <section className="space-y-3" aria-labelledby="roles-reference-heading">
              <div>
                <div className="flex items-center gap-2">
                  <Lock size={14} className="text-fieldiq-muted" aria-hidden="true" />
                  <h2 id="roles-reference-heading" className="text-sm font-semibold text-white">Role reference</h2>
                  <span className="rounded-full border border-fieldiq-border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">
                    Read only
                  </span>
                </div>
                <p className="text-xs text-fieldiq-muted mt-1">
                  The built-in roles and what each can do. They are built in, and nothing on this page changes them. A person&apos;s role is set on Team.
                </p>
              </div>
              <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl overflow-hidden">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-fieldiq-border">
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-fieldiq-muted w-1/3">
                        Role
                      </th>
                      <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-fieldiq-muted">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-4 py-6 text-center text-sm text-fieldiq-muted">
                          No roles available.
                        </td>
                      </tr>
                    ) : (
                      roles.map((r) => (
                        <tr
                          key={r.key}
                          className="border-b border-fieldiq-border last:border-b-0"
                        >
                          <td className="px-4 py-3 align-top text-sm font-medium text-white">
                            {r.label}
                          </td>
                          <td className="px-4 py-3 align-top text-sm text-fieldiq-text">
                            {r.description}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* THE CONTROL — dispatch elevation toggle, copy from the API. It sits
              * BELOW the reference because the API's Dispatcher row says "via the
              * toggle below". */}
            {copy && (
              <section className="space-y-3" aria-labelledby="roles-setting-heading">
                <div className="flex items-center gap-2">
                  <ToggleRight size={16} className="text-fieldiq-accent" aria-hidden="true" />
                  <h2 id="roles-setting-heading" className="text-sm font-semibold text-white">Setting</h2>
                  <span className="text-xs text-fieldiq-muted">Saves when you flip it</span>
                </div>
                <div className="flex items-center justify-between bg-fieldiq-card border border-fieldiq-border border-l-4 border-l-fieldiq-accent rounded-xl p-4">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-white">{copy.toggle_label}</p>
                    <p className="text-xs text-fieldiq-muted mt-0.5">
                      {copy.toggle_description}
                    </p>
                    {!canToggle && (
                      <p className="text-xs text-fieldiq-muted mt-2">{refusal}</p>
                    )}
                  </div>
                  <button
                    role="switch"
                    aria-checked={enabled}
                    aria-label={copy.toggle_label}
                    onClick={handleToggle}
                    disabled={!canToggle || saveMutation.isPending}
                    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      enabled ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                        enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Admin-only note — rendered from the API copy */}
                <div className="flex items-start gap-2 rounded-lg bg-fieldiq-dark border border-fieldiq-border px-3 py-2.5">
                  <Info size={14} className="text-fieldiq-muted mt-0.5 shrink-0" />
                  <p className="text-xs text-fieldiq-muted">{copy.admin_only_note}</p>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
